import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Target, Award, Users, Globe, Zap, Shield, ArrowRight } from 'lucide-react';
import SEO from '@/components/SEO';

const AboutUs = () => {
  const values = [
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Connecting your stories with audiences across 50+ countries worldwide'
    },
    {
      icon: Zap,
      title: 'Speed & Efficiency',
      description: 'Fast-track your news distribution with our streamlined process'
    },
    {
      icon: Shield,
      title: 'Transparency',
      description: 'No hidden fees, no complicated procedures - just results'
    }
  ];

  const whoWeServe = [
    { title: 'Startups', description: 'Looking to attract investors or launch new products' },
    { title: 'Growing Businesses', description: 'Entering new markets or announcing partnerships' },
    { title: 'Enterprises', description: 'Strengthening corporate communication and thought leadership' },
    { title: 'Non-profits & NGOs', description: 'Highlighting initiatives and raising public awareness' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About Us"
        description="Learn about Ixoraly, the professional press release distribution platform helping businesses amplify their news to global media outlets and journalists."
        path="/about-us"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="about-heading">
              About Ixoraly
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              At Ixoraly, we believe that <strong className="text-white">every story deserves to be seen, heard, and recognized</strong>. In today's fast-paced digital era, information travels quickly — yet meaningful stories often get lost in the noise.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Businesses innovate every day, startups disrupt industries, non-profits create real social impact, and organizations achieve milestones worth celebrating. But without the right exposure, even the most powerful news can remain unnoticed.
            </p>
            <p className="text-xl text-navy-600 font-semibold mb-8">
              That's exactly where Ixoraly steps in.
            </p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              We are a <strong>global press release distribution and media outreach company</strong>, built to help brands share their achievements with the world. From emerging entrepreneurs to established multinational companies, we ensure that every announcement reaches the right media professionals, journalists, influencers, investors, and audiences — locally and internationally.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our platform combines <strong>targeted syndication, search engine visibility, and newsroom publication</strong> to give your news the spotlight it truly deserves. We take pride in being the bridge between creators of great stories and the people who want to read them.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 bg-navy-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What We Do</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              We help organizations distribute their press releases across a wide network of credible online media, news websites, industry publications, and search engines. Whether you are announcing a <strong>product launch, business expansion, funding round, acquisition, event update, social initiative, or expert insights</strong>, our system ensures that your content becomes discoverable to the people who matter most.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              With the power of strategic distribution and smart digital visibility, we transform a simple press release into a <strong>powerful branding tool</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Why We Exist */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why We Exist</h2>
            <p className="text-lg text-gray-700 mb-8">
              Many brands struggle with PR because traditional media outreach can be expensive, slow, and complicated. We created Ixoraly to eliminate these roadblocks.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="p-8 bg-white rounded-xl border-2 border-gray-100 hover:border-navy-200 hover:shadow-xl transition-all duration-300">
                  <div className="w-14 h-14 bg-navy-100 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="text-navy-600" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>

          <div className="max-w-4xl mx-auto mt-12">
            <p className="text-lg text-gray-700 mb-4">
              We believe in <strong>equal opportunity visibility</strong>. Whether you are a solo founder running your first startup or a global company expanding into new markets, your story has value. And we are here to make sure the world sees it.
            </p>
          </div>
        </div>
      </section>

      {/* Our Promise */}
      <section className="py-16 bg-navy-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Promise</h2>
            <p className="text-2xl font-semibold text-coral-600 mb-8">Visibility. Credibility. Impact.</p>
            <p className="text-lg text-gray-700 mb-6">Our press release distribution is designed to drive:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Brand Awareness</span>
                  <p className="text-sm text-gray-600">More people learn who you are and what you offer</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Trust & Authority</span>
                  <p className="text-sm text-gray-600">Media mentions elevate reputation</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Search Engine Discoverability</span>
                  <p className="text-sm text-gray-600">Better SEO ranking and online presence</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Audience Engagement</span>
                  <p className="text-sm text-gray-600">Your message reaches interested readers</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 md:col-span-2">
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <span className="font-semibold text-gray-900">Long-Term Value</span>
                  <p className="text-sm text-gray-600">News lives online permanently, strengthening digital footprint</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white border-2 border-gray-100 rounded-2xl p-8 hover:border-navy-200 hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-navy-100 rounded-xl flex items-center justify-center mb-6">
                <Target className="text-navy-600" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-700 leading-relaxed">
                To empower organizations of all sizes with <strong>accessible, transparent, and highly effective</strong> press release distribution services that drive awareness, credibility, and measurable engagement. We are committed to ensuring that impactful stories never go unheard simply due to budget or network limitations.
              </p>
            </div>
            <div className="bg-white border-2 border-gray-100 rounded-2xl p-8 hover:border-navy-200 hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-navy-100 rounded-xl flex items-center justify-center mb-6">
                <Award className="text-navy-600" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-700 leading-relaxed">
                To become the world's <strong>most trusted communications partner</strong>, connecting news with the right audiences everywhere — ensuring that a brand's story becomes part of the global conversation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-16 bg-navy-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Who We Serve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whoWeServe.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-navy-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="text-navy-600" size={24} />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ixoraly — Where Every Announcement Becomes a Global Headline
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Ready to share your story with the world?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/submit-press-release"
              data-testid="about-cta-button"
              className="inline-flex items-center justify-center px-8 py-4 bg-coral-600 text-white rounded-lg font-semibold hover:bg-coral-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Submit Your Press Release
              <ArrowRight size={20} className="ml-2" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-white/20 transition-all duration-200 border border-white/20"
            >
              View Pricing Plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
